import React from 'react'
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Dimensions,
} from 'react-native'
import { LayoutDashboard, LayoutGrid, Users, Wallet, Settings } from 'lucide-react-native'
import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RootStackParamList } from '../types/navigation'
import { useLanguage } from '@fliisi/i18n'
import { useNavShell } from '../contexts/NavShellContext'
import theme from '../theme'

const { width } = Dimensions.get('window')
const isDesktop = width > 768

interface Props {
  marketId: string
}

type NavItem = {
  key: string
  icon: React.ComponentType<{ size: number; color: string }>
  labelKey: string
  route: keyof RootStackParamList
  getParams?: () => object
}

export default function MarketBottomNav({ marketId }: Props) {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>()
  const { routeName } = useNavShell()
  const { t } = useLanguage()

  const navItems: NavItem[] = [
    {
      key: 'dashboard',
      icon: LayoutDashboard,
      labelKey: 'dashboard',
      route: 'MarketAdmin',
      getParams: () => ({ marketId }),
    },
    {
      key: 'spaces',
      icon: LayoutGrid,
      labelKey: 'spaces',
      route: 'MarketSpaces',
      getParams: () => ({ marketId }),
    },
    {
      key: 'sellers',
      icon: Users,
      labelKey: 'sellers',
      route: 'MarketSellers',
      getParams: () => ({ marketId }),
    },
    {
      key: 'payouts',
      icon: Wallet,
      labelKey: 'payouts',
      route: 'MarketPayouts',
      getParams: () => ({ marketId }),
    },
    {
      key: 'settings',
      icon: Settings,
      labelKey: 'settings',
      route: 'MarketSettings',
      getParams: () => ({ marketId }),
    },
  ]

  const isActive = (item: NavItem) => {
    if (item.route === routeName) return true
    return false
  }

  const handleNavPress = (item: NavItem) => {
    const params = item.getParams ? item.getParams() : undefined
    navigation.navigate(item.route as any, params as any)
  }

  return (
    <View style={[styles.container, isDesktop && styles.containerDesktop]}>
      <View style={[styles.nav, isDesktop && styles.navDesktop]}>
        {navItems.map((item) => {
          const active = isActive(item)
          const IconComponent = item.icon
          const iconColor = active ? theme.colors.merchant.primary : theme.colors.text.secondary
          return (
            <TouchableOpacity
              key={item.key}
              style={[styles.navItem, active && styles.navItemActive]}
              onPress={() => handleNavPress(item)}
            >
              <IconComponent size={22} color={iconColor} />
              <Text style={[styles.navLabel, active && styles.navLabelActive]}>
                {t(item.labelKey)}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.background.card,
    borderTopWidth: 1,
    borderTopColor: theme.colors.merchant.border,
    paddingBottom: Platform.OS === 'ios' ? 20 : 0,
    ...(Platform.OS === 'web' ? { boxShadow: '0 -2px 8px rgba(192,122,95,0.1)' } : {
      shadowColor: theme.colors.merchant.primary,
      shadowOffset: { width: 0, height: -2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 8,
    }),
  },
  containerDesktop: {
    borderTopWidth: 0,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.merchant.border,
    paddingBottom: 0,
    ...(Platform.OS === 'web' ? { boxShadow: '0 2px 8px rgba(192,122,95,0.1)' } : {
      shadowOffset: { width: 0, height: 2 },
    }),
  },
  nav: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingVertical: 8,
  },
  navDesktop: {
    justifyContent: 'center',
    gap: 24,
    maxWidth: 700,
    marginHorizontal: 'auto',
  },
  navItem: {
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 4,
  },
  navItemActive: {
    // Active state handled by text styles
  },
  navLabel: {
    fontSize: 10,
    color: theme.colors.text.secondary,
    fontWeight: '500',
    marginTop: 2,
  },
  navLabelActive: {
    color: theme.colors.merchant.primary,
    fontWeight: '600',
  },
})
